import { IMessageResult } from '../interfaces/IMessageResult';
import { IMessageFilter } from '../interfaces/IMessageFilter';
import { Helper } from '../Helper';
import { Environment } from './Environment';
import { IMessage } from '../interfaces/IMessage';
import { IMessageTrackingData } from '../interfaces/IMessageTrackingData';
import { ISPMessage } from '../interfaces/ISPMessage';
import { FetchProxy } from 'intraactive-sdk-helper';
import { sdkContainerInstance } from '../global/Container';
import { IMessageTrackingDataOverview } from '../interfaces/IMessageTrackingDataOverview';
import { IPushMessageOverviewTrackingState } from '../components/contentCreator/pushMessageItem/PushMessageItem';
import { IInstance } from '../interfaces/IInstance';
import { IHttpStatus } from '../interfaces/IHttpStatus';
import DateTimeService from './DateTimeService';
import { ITargetingGroupOptions } from '../interfaces/ITargetingGroupOptions';
import { IEnvironment } from '../interfaces/IEnvironment';
import { IMessageCanCreateMorePins } from '../interfaces/IMessageCanCreateMorePins';
import { IDigestData } from '../interfaces/IDigestData';

export default class MessageService {

  private static convertSPMessageToMessage(SPMessage: ISPMessage): IMessage {
    let message: IMessage = {};
    message.id = SPMessage.id;
    message.title = SPMessage.title;
    message.teaser = SPMessage.iaTeaser;
    message.videoUrl = SPMessage.iaMessageVideoUrl;
    message.videoEmbedCode = SPMessage.iaMessageVideoEmbedCode;
    message.content = SPMessage.iaMessageTemplateData && SPMessage.iaMessageTemplateData.substring(0, 1) !== "{" ? SPMessage.iaMessageTemplateData : SPMessage.iaMessageContent;
    message.image = SPMessage.iaMessageBgImage;
    message.categories = SPMessage.iaMessageCategories;

    // We need to calculate the correct start and end date for recurring events.
    // The API returns in UTC, but we need to consider standard and daylight saving time for the user
    const dateTimeService = new DateTimeService();

    if (SPMessage.iaMessageStartTime) {
      message.startDate = new Date(SPMessage.recurrence
        ? dateTimeService.getTimeZoneAdjustedDate(SPMessage.modified, SPMessage.iaMessageStartTime)
        : SPMessage.iaMessageStartTime);
    }
    if (SPMessage.iaMessageEndTime) {
      message.endDate = new Date(SPMessage.recurrence
        ? dateTimeService.getTimeZoneAdjustedDate(SPMessage.modified, SPMessage.iaMessageEndTime)
        : SPMessage.iaMessageEndTime);
    }
    if (SPMessage.modified) {
      message.modified = new Date(SPMessage.modified);
    }
    if (SPMessage.created) {
      message.created = new Date(SPMessage.created);
    }
    if (SPMessage.author) {
      message.author = {
        id: SPMessage.author.id,
        displayName: SPMessage.author.name,
        userPrincipalName: SPMessage.author.userPrincipalName,
        email: SPMessage.author.email,
        image: SPMessage.author.photo
      };
    }
    if (SPMessage.editor) {
      message.editor = {
        id: SPMessage.editor.id,
        displayName: SPMessage.editor.name,
        userPrincipalName: SPMessage.editor.userPrincipalName,
        email: SPMessage.editor.email,
        image: SPMessage.editor.photo
      };
    }
    if (SPMessage.iaMessageContact && SPMessage.iaMessageContact.length > 0) {
      message.contacts = SPMessage.iaMessageContact;
      message.contacts.forEach((contact: any) => {
        contact = Helper.syncUserProperties(contact);
      });
    }
    if (SPMessage.iaApprovers && SPMessage.iaApprovers.length > 0) {
      message.approvers = SPMessage.iaApprovers;
      message.approvers.forEach((approver: any) => {
        approver = Helper.syncUserProperties(approver);
      });
    }

    message.likes = [];
    message.commentCount = 0;

    message.push = SPMessage.iaMessagePush;
    message.sendUsingBot = SPMessage.iaMessageSendUsingBot;

    message.iaTargetingOperator = SPMessage.iaTargetingOperator;
    message.iaTargetingOperatorADGroups = SPMessage.iaTargetingOperatorADGroups;
    message.iaTargetingTargetContent = SPMessage.iaTargetingTargetContent;
    message.iaTargetingDepartment = SPMessage.iaTargetingDepartment;
    message.iaTargetingJobTitle = SPMessage.iaTargetingJobTitle;
    message.iaTargetingLocation = SPMessage.iaTargetingLocation;
    message.iaTargetingCustom1 = SPMessage.iaTargetingCustom1;
    message.iaTargetingCustom2 = SPMessage.iaTargetingCustom2;
    message.targetingFields = SPMessage.targetingFields;
    message.iaTargetingGroupData = SPMessage.iaMessageTargetingGroupData;
    message.targetingExistingStructure = SPMessage.targetingExistingStructure;
    message.isDraft = SPMessage.iaDraft;
    message.approvalStatus = SPMessage.iaApprovalStatus;
    message.approvers = SPMessage.iaApprovers;
    message.isRead = SPMessage.isRead;
    message.feeds = SPMessage.iaFeeds;
    message.isPinned = SPMessage.iaIsPinned;
    message.pinnedEndDate = SPMessage.iaPinEndDateTime ? new Date(SPMessage.iaPinEndDateTime) : undefined;
    message.recurrence = SPMessage.recurrence;
    message.recurrenceData = SPMessage.recurrenceData;
    message.recurrenceEndDate = SPMessage.IARecurrenceEndDateTime;

    message.iaConfiguration = SPMessage.iaConfiguration;
    if (!message.iaConfiguration) {
      message.iaConfiguration = {};
    }

    message.iaMessageBgImagesFromBlob = SPMessage.iaMessageBgImagesFromBlob;

    return message;
  }

  private static convertMessageToSPMessage(message: IMessage): ISPMessage {
    let SPMessage: ISPMessage = {};
    SPMessage.id = message.id;
    SPMessage.title = message.title;
    SPMessage.iaMessageContent = message.content;
    SPMessage.iaMessageTemplateData = message.content;
    SPMessage.iaMessagePush = message.push;
    SPMessage.iaMessageSendUsingBot = message.sendUsingBot;
    SPMessage.iaMessageStartTime = message.startDate;
    SPMessage.iaMessageEndTime = message.endDate;
    SPMessage.iaMessageContact = message.contacts;
    SPMessage.iaTargetingOperator = message.iaTargetingOperator;
    SPMessage.iaTargetingOperatorADGroups = message.iaTargetingOperatorADGroups;
    SPMessage.iaTargetingTargetContent = message.iaTargetingTargetContent;
    SPMessage.created = message.created;
    SPMessage.author = message.author;
    SPMessage.editor = message.editor;
    SPMessage.iaMessageBgImage = message.image;
    SPMessage.iaMessageVideoUrl = message.videoUrl;
    SPMessage.iaMessageVideoEmbedCode = message.videoEmbedCode;
    SPMessage.modified = message.modified;
    SPMessage.iaTargetingDepartment = message.iaTargetingDepartment;
    SPMessage.iaTargetingJobTitle = message.iaTargetingJobTitle;
    SPMessage.iaTargetingLocation = message.iaTargetingLocation;
    SPMessage.iaTargetingCustom1 = message.iaTargetingCustom1;
    SPMessage.iaTargetingCustom2 = message.iaTargetingCustom2;
    SPMessage.iaMessageCategories = message.categories;
    SPMessage.iaMessageTargetingGroupData = message.iaTargetingGroupData;
    SPMessage.targetingFields = message.targetingFields;
    SPMessage.targetingExistingStructure = message.targetingExistingStructure;
    SPMessage.iaDraft = message.isDraft;
    SPMessage.iaApprovalStatus = message.approvalStatus;
    SPMessage.iaApprovers = message.approvers?.filter(i => !i.disabled);
    SPMessage.iaConfiguration = message.iaConfiguration;
    SPMessage.iaTeaser = message.teaser;
    SPMessage.iaFeeds = message.feeds;
    SPMessage.iaIsPinned = message.isPinned;
    SPMessage.iaPinEndDateTime = message.pinnedEndDate;
    SPMessage.recurrence = message.recurrence;
    SPMessage.recurrenceData = message.recurrenceData;
    return SPMessage;
  }

  public static getActivePinnedMessages(environment: IEnvironment, token: string, tenant: string, instance: string, targeting: boolean, filter: IMessageFilter, isFiltered: boolean, search?: string): Promise<IMessageResult> {
    // add filters for pinned messages
    const startDate = new Date();
    filter.FilteredBy.push({
      fields: [
        {
          field: "IAPinEndDateTime",
          type: "DateTime",
          value: startDate.toISOString(),
          condition: "Or",
          operator: "GreaterThanOrEqual"
        },
        {
          field: "IAPinEndDateTime",
          type: "DateTime",
          value: "",
          condition: "Or",
          operator: "IsNull"
        }
      ],
      condition: "And"
    });
    filter.FilteredBy.push({
      fields: [
        {
          field: "IAIsPinned",
          value: 1,
          condition: "Or",
          operator: "Equal"
        }
      ],
      condition: "And"
    });
    filter.RowLimit = 19;
    return this.getMessages(environment, token, tenant, instance, targeting, filter, isFiltered, search, false);
  }

  public static getPinnedMessages(environment: IEnvironment, token: string, tenant: string, instance: string, targeting: boolean, filter: IMessageFilter, isFiltered: boolean, search?: string): Promise<IMessageResult> {
    filter.FilteredBy.push({
      fields: [
        {
          field: "IAIsPinned",
          value: 1,
          condition: "Or",
          operator: "Equal"
        }
      ],
      condition: "And"
    });
    filter.RowLimit = 19;
    return this.getMessages(environment, token, tenant, instance, targeting, filter, isFiltered, search, false);
  }

  public static getMessages(environment: IEnvironment, token: string, tenant: string, instance: string, targeting: boolean, filter: IMessageFilter, isFiltered: boolean, search?: string, excludePinnedMessages: boolean = true): Promise<IMessageResult> {
    return new Promise<IMessageResult>((resolve: (result: IMessageResult) => void): void => {
      if (environment && token && tenant && instance && filter) {
        let apiUrl = Environment.getEnvironmentForMessagesAPI(environment) + `/${tenant}/${instance}/messages?useTargeting=${targeting}`;
        if (search) {
          apiUrl += `&textSearch=${search}`;
        }
        if (excludePinnedMessages) {
          const startDate = new Date();
          filter.FilteredBy.push({
            fields: [
              {
                field: "IAPinEndDateTime",
                type: "DateTime",
                value: startDate.toISOString(),
                condition: "Or",
                operator: "LessThanOrEqual"
              },
              {
                field: "IAPinEndDateTime",
                type: "DateTime",
                value: "",
                condition: "Or",
                operator: "IsNull"
              },
              {
                field: "IAIsPinned",
                value: 0,
                condition: "Or",
                operator: "Equal"
              },
              {
                field: "IAIsPinned",
                value: null,
                condition: "Or",
                operator: "IsNull"
              }
            ],
            condition: "And"
          });
        }
        // check if default filter params are applied
        const defaultFilterParams: string[] = ["IAMessageEndTime", "IAMessageStartTime", "IADraft"];
        let hasDefaultFilterParams: boolean = isFiltered ? false : true;
        if (!isFiltered) {
          for (let i = 0; i < filter.FilteredBy.length; i++) {
            if (defaultFilterParams.indexOf(filter.FilteredBy[i].fields[0].field) === -1) {
              hasDefaultFilterParams = false;
              break;
            }
          }
        }
        const isDefaultFiltered: boolean = !isFiltered && filter.FilteredBy.length === 3 && hasDefaultFilterParams;
        const body: string = !filter ? null : JSON.stringify(filter);
        new FetchProxy(
          apiUrl, {
          method: "POST",
          headers: {
            'AuthToken': token,
            'Content-Type': 'application/json'
          },
          body: body
        })
          .withContainer(sdkContainerInstance)
          .onKnownHttpError((message, response) => {
            resolve({
              pagingInfo: undefined,
              messages: undefined,
              httpStatus: {
                statusCode: response.status,
                message: message || response.statusText
              }
            });
          })
          .fetch()
          .then((response) => {
            if (response.status === 204) {
              resolve({
                pagingInfo: undefined,
                messages: undefined,
                httpStatus: {
                  statusCode: response.status,
                  message: response.statusText
                }
              });
            } else {
              return response.json().then((jsonResponse: any) => {
                let messages = [];
                jsonResponse.result.forEach((spMessage: ISPMessage) => {
                  let message: IMessage = this.convertSPMessageToMessage(spMessage);
                  if (message.content) {
                    message.content = message.content.replace(/<img[^>]*>/g, "");
                  }
                  messages.push(message);
                });

                let messageResult: IMessageResult = {
                  pagingInfo: jsonResponse.pagingInfo,
                  messages: messages,
                  httpStatus: {
                    statusCode: 200
                  },
                  isDefaultFiltered: isDefaultFiltered,
                  searchValue: apiUrl.split('?')[1]?.split('&')[1]?.split('=')[1]
                }
                resolve(messageResult);
              });
            }
          })
          .catch(error => {
            console.log(error);
            resolve({
              pagingInfo: undefined,
              messages: undefined,
              httpStatus: {
                statusCode: 500,
                message: error.message
              }
            });
          });
      } else {
        resolve(null);
      }
    });
  }

  public static getMessage(environment: IEnvironment, token: string, tenant: string, instance: string, messageId: number): Promise<IMessageResult> {
    return new Promise<IMessageResult>((resolve: (result: IMessageResult) => void): void => {
      if (environment && token && tenant && instance && messageId) {
        let apiUrl = Environment.getEnvironmentForMessagesAPI(environment) + `/${tenant}/${instance}/messages?useTargeting=false`;
        let filter = [];

        filter.push({
          fields: [
            {
              field: "ID",
              type: "Number",
              value: messageId,
              condition: "And",
              operator: "Equal"
            }
          ],
          condition: "And"
        });
        const body: string = JSON.stringify(
          {
            FilteredBy: filter,
            RowLimit: 0,
            PageInfo: undefined,
            OrderedBy: undefined
          }
        );
        new FetchProxy(
          apiUrl, {
          method: "POST",
          headers: {
            'AuthToken': token,
            'Content-Type': 'application/json'
          },
          body: body
        })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              if (response.status === 404) {
                resolve({
                  pagingInfo: undefined,
                  messages: undefined,
                  httpStatus: {
                    statusCode: response.status,
                    message: response.statusText
                  }
                });
              }
              return response.json().then((jsonResponse: any) => {
                resolve({
                  pagingInfo: undefined,
                  messages: undefined,
                  httpStatus: {
                    statusCode: response.status,
                    message: jsonResponse.message != undefined ? jsonResponse.message : jsonResponse
                  }
                });
              });
            }
            return response.json().then((jsonResponse: any) => {
              let messages = [];
              jsonResponse.result.forEach((spMessage: ISPMessage) => {
                let message: IMessage = this.convertSPMessageToMessage(spMessage);
                if (message.content) {
                  message.content = message.content.replace(/<img[^>]*>/g, "");
                }
                messages.push(message);
              });
              let messageResult: IMessageResult = {
                pagingInfo: jsonResponse.pagingInfo,
                messages: messages,
                httpStatus: {
                  statusCode: 200
                }
              }
              resolve(messageResult);
            });
          })
          .catch(error => {
            console.log(error);
            resolve({
              pagingInfo: undefined,
              messages: undefined,
              httpStatus: {
                statusCode: 500,
                message: error.message
              }
            });
          });
      } else {
        resolve(null);
      }
    });
  }

  public static createMessage(message: IMessage, environment: IEnvironment, token: string, tenant: string, instance: string, targetingGroupOptions: ITargetingGroupOptions): Promise<{ httpStatus: IHttpStatus, id?: number }> {
    return new Promise<{ httpStatus: IHttpStatus, id?: number }>((resolve: (result: { httpStatus: IHttpStatus, id?: number }) => void): void => {
      if (message && environment && token && tenant && instance) {
        const targeting = targetingGroupOptions ? `?targetingGroupOptions=${JSON.stringify(targetingGroupOptions)}` : "";
        let apiUrl = `${Environment.getEnvironmentForMessagesAPI(environment)}/${tenant}/${instance}${targeting}`;
        let SPMessage = this.convertMessageToSPMessage(message);
        if (SPMessage.iaMessageContact != undefined) {
          SPMessage.iaMessageContact.forEach((contact: any) => {
            contact = Helper.syncUserProperties(contact);
          });
        }
        if (SPMessage.iaApprovers != undefined) {
          SPMessage.iaApprovers.forEach((approver: any) => {
            approver = Helper.syncUserProperties(approver);
          });
        }
        const body: string = JSON.stringify(SPMessage);
        new FetchProxy(apiUrl, { method: "POST", headers: { 'Content-Type': 'application/json', 'AuthToken': token }, body: body })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              throw (response);
            }
            return response.json().then((message) => {
              resolve({
                httpStatus: {
                  statusCode: 200,
                },
                id: message.id
              });
            });
          })
          .catch(error => {
            console.log(error);
            resolve({
              httpStatus: {
                statusCode: error.status,
                message: error.statusText
              }
            });
          });
      } else {
        resolve({
          httpStatus: {
            statusCode: 400,
            message: "Missing parameter"
          }
        });
      }
    });
  }

  public static updateMessage(message: IMessage, environment: IEnvironment, token: string, tenant: string, instance: string, targetingGroupOptions: ITargetingGroupOptions, isPublishedFirstTime: boolean = false): Promise<IHttpStatus> {
    return new Promise<IHttpStatus>((resolve: (httpStatus: IHttpStatus) => void): void => {
      if (message && environment && token && tenant && instance) {
        const targeting = targetingGroupOptions ? `&targetingGroupOptions=${JSON.stringify(targetingGroupOptions)}` : "";
        let apiUrl = `${Environment.getEnvironmentForMessagesAPI(environment)}/${tenant}/${instance}/${message.id}?isBeingPublished=${isPublishedFirstTime}${targeting}`;
        let SPMessage = this.convertMessageToSPMessage(message);
        if (SPMessage.iaMessageContact != undefined && SPMessage.iaMessageContact[0] != undefined) {
          SPMessage.iaMessageContact.forEach((contact: any) => {
            contact = Helper.syncUserProperties(contact);
          });
        } else {
          SPMessage.iaMessageContact = undefined;
        }
        if (SPMessage.iaApprovers != undefined && SPMessage.iaApprovers[0] != undefined) {
          SPMessage.iaApprovers.forEach((approver: any) => {
            approver = Helper.syncUserProperties(approver);
          });
        } else {
          SPMessage.iaApprovers = undefined;
        }
        const body: string = JSON.stringify(SPMessage);
        new FetchProxy(apiUrl, { method: "PUT", headers: { 'Content-Type': 'application/json', 'AuthToken': token }, body: body })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              throw (response);
            }
            return resolve({
              statusCode: 200
            });
          })
          .catch(error => {
            console.log(error);
            resolve({
              statusCode: error.status,
              message: error.statusText
            });
          });
      } else {
        resolve({
          statusCode: 400,
          message: "Missing parameter"
        });
      }
    });
  }

  public static deleteMessage(id: string, environment: IEnvironment, token: string, tenant: string, instance: string): Promise<string> {
    return new Promise<string>((resolve: (status: string) => void, reject: (error: any) => void): void => {
      if (id && environment && token && tenant && instance) {
        let apiUrl = `${Environment.getEnvironmentForMessagesAPI(environment)}/${tenant}/${instance}/${id}`;
        new FetchProxy(apiUrl, { method: "DELETE", headers: { 'Content-Type': 'application/json', 'AuthToken': token } })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              reject(response);
              return;
            }
            return resolve(response.statusText);
          })
          .catch(error => {
            console.log(error);
            reject(null);
          });
      } else {
        reject(null);
      }
    });
  }

  public static sendPushMessage(id: number, environment: IEnvironment, tenant: string, token: string, instance: string): Promise<IHttpStatus> {
    let api = `${Environment.getEnvironmentForMainAPI(environment)}/api/${tenant}.sharepoint.com/IntraActive-Messages/notification/sendpushmessage/${id}?appId=1&instance=${instance}`;
    return new Promise<IHttpStatus>((resolve: (httpStatus: IHttpStatus) => void): void => {
      if (id && environment && token && tenant && instance) {
        new FetchProxy(api, {
          credentials: "same-origin",
          headers: { 'Content-Type': 'application/json', 'AuthToken': token }
        })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              throw (response);
            }
            return resolve({
              statusCode: 200
            });
          })
          .catch(error => {
            console.log(error);
            resolve({
              statusCode: error.status,
              message: error.statusText
            })
          });
      } else {
        resolve({
          statusCode: 400,
          message: "Missing parameter"
        });
      }
    });
  }

  public static getNodes = (nodes, list) => {
    nodes.forEach(node => {
      if (node.checked) {
        list.push(node.defaultLabel);
        if (node.childNodes && node.childNodes.length > 0) {
          MessageService.getNodes(node.childNodes, list);
        }
      }
    });
    return list;
  }

  public static getInstanceSettings(instance: string, environment, tenant: string, token: string, ignoreCache?: boolean): Promise<{ httpStatus: IHttpStatus, instance: IInstance }> {
    let api = `${Environment.getEnvironmentForMainAPI(environment)}/api/instances/getInstance/${tenant}.sharepoint.com/IntraActive-Messages/?instanceName=${instance}`;
    return new Promise<{ httpStatus: IHttpStatus, instance: IInstance }>((resolve: (status: any) => void): void => {
      if (environment && token && tenant && instance) {
        const instanceSettings = localStorage.getItem(`IA_instanceSettingsMessages_${instance}`);
        if (!ignoreCache && instanceSettings && new DateTimeService().getDatePlusOneHour(new Date(JSON.parse(instanceSettings).date)) > new Date()) {
          console.log("Using cached instance settings");
          resolve(JSON.parse(instanceSettings).value);
        } else {
          console.log("Fetching instance settings from server");
          new FetchProxy(api, {
            credentials: "same-origin",
            headers: { 'Content-Type': 'application/json', 'AuthToken': token }
          })
            .withContainer(sdkContainerInstance)
            .fetch()
            .then((response) => {
              if (!response.ok) {
                throw (response);
              }
              return response.json().then(settings => {
                Helper.setLocalStorage(`IA_instanceSettingsMessages_${instance}`, JSON.stringify({
                  date: new Date(),
                  value: {
                    httpStatus: {
                      statusCode: 200,
                    },
                    instance: settings
                  }
                }));
                resolve({
                  httpStatus: {
                    statusCode: 200,
                  },
                  instance: settings
                });
              });
            })
            .catch(error => {
              console.log(error);
              resolve({
                httpStatus: {
                  statusCode: error.status,
                  message: error.statusText
                }
              });
            });
        }
      } else {
        resolve({
          httpStatus: {
            statusCode: 400,
            message: "Missing parameter"
          }
        });
      }
    });
  }

  public static getInstances(token: string, tenant: string): Promise<any[]> {
    return new Promise((resolve) => {
      const url = `${process.env.APIUrl}/api/instances/getInstances/${tenant}.sharepoint.com/IntraActive-Messages`;
      return new FetchProxy(url, {
        credentials: "same-origin",
        mode: "cors",
        headers: {
          "AuthToken": token
        }
      })
        .withContainer(sdkContainerInstance)
        .fetch()
        .then((response) => {
          if (!response.ok) {
            throw (response.status);
          } else {
            return response.json();
          }
        }).then(response => {
          console.log(response);
          resolve(response);
        }).catch((error) => {
          console.log(error);
        });
    });
  }

  public static readMessage(environment: IEnvironment, token: string, tenant: string, instance: string, messageId: number): Promise<any> {
    return new Promise<any>((resolve: (status: string) => void, reject: (error: any) => void): void => {
      if (messageId && environment && token && tenant && instance) {
        let apiUrl = `${Environment.getEnvironmentForMessagesAPI(environment)}/${tenant}/${instance}/message/${messageId}/read`;
        new FetchProxy(apiUrl, { method: "POST", headers: { 'Content-Type': 'application/json', 'AuthToken': token } })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              reject(response);
              return;
            }
            return resolve(response.statusText);
          })
          .catch(error => {
            console.log(error);
            reject(null);
          });
      } else {
        reject(null);
      }
    });
  }

  public static readAllMessages(environment: IEnvironment, token: string, tenant: string, instance: string): Promise<any> {
    return new Promise<any>((resolve: (status: string) => void, reject: (error: any) => void): void => {
      if (environment && token && tenant && instance) {
        let apiUrl = `${Environment.getEnvironmentForMessagesAPI(environment)}/${tenant}/${instance}/read`;
        new FetchProxy(apiUrl, { method: "POST", headers: { 'Content-Type': 'application/json', 'AuthToken': token } })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              reject(response);
              return;
            }
            return resolve(response.statusText);
          })
          .catch(error => {
            console.log(error);
            reject(null);
          });
      } else {
        reject(null);
      }
    });
  }

  public static async setMessageAuthorOrEditor(environment: IEnvironment, token: string, tenant: string, component: string, instance: string, message: IMessage, userRole: "author" | "editor") {
    try {
      const userPrincipalName = await Helper.getUPNByLookupId(environment, token, tenant, component, instance, message[userRole].id)
      const image = Helper.getProfileImageUrlFromImageAPI(Environment.getEnvironmentForImageAPI(environment), tenant, token, userPrincipalName || message.author.email);
      const updatedUser = { ...message[userRole], userPrincipalName, image };
      message[userRole] = { ...updatedUser };
    }
    catch {
      // if error occurs, author and editor fields are not updated
    }
  }

  public static getMessageTrackingOverview(environment: IEnvironment, token: string, tenant: string, instance: string, messageId: number): Promise<IMessageTrackingDataOverview> {
    return new Promise<any>((resolve: (status: string) => void, reject: (error: any) => void): void => {
      if (environment && token && tenant && instance && messageId) {
        let apiUrl = `${Environment.getEnvironmentForMessagesAPI(environment)}/${tenant}/tracking/${instance}/${messageId}/overview`;
        new FetchProxy(apiUrl, { method: "GET", headers: { 'Content-Type': 'application/json', 'AuthToken': token } })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              throw (response.status);
            } else {
              return response.json();
            }
          }).then(response => {
            resolve(response);
          })
          .catch(error => {
            console.log(error);
            reject(null);
          });
      } else {
        reject(null);
      }
    });
  }

  public static getMessageTrackingData(environment: IEnvironment, token: string, tenant: string, instance: string, messageId: number, from: number, count: number, searchTerm: string, trackingState: IPushMessageOverviewTrackingState): Promise<IMessageTrackingData> {
    return new Promise<any>((resolve: (status: string) => void, reject: (error: any) => void): void => {
      if (environment && token && tenant && instance && messageId && from !== undefined && count !== undefined) {
        let apiUrl = `${Environment.getEnvironmentForMessagesAPI(environment)}/${tenant}/tracking/${instance}/${messageId}?from=${from}&count=${count}&searchQuery=${searchTerm}&overviewTrackingState=${trackingState}`;
        new FetchProxy(apiUrl, { method: "GET", headers: { 'Content-Type': 'application/json', 'AuthToken': token } })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              throw (response.status);
            } else {
              return response.json();
            }
          }).then(response => {
            resolve(response);
          })
          .catch(error => {
            console.log(error);
            reject(null);
          });
      } else {
        reject(null);
      }
    });
  }

  public static getCanCreateMorePins(environment: IEnvironment, token: string, tenant: string, instance: string): Promise<IMessageCanCreateMorePins> {
    return new Promise<IMessageCanCreateMorePins>((resolve, reject): void => {
      if (environment && token && tenant && instance) {
        let apiUrl = `${Environment.getEnvironmentForMessagesAPI(environment)}/${tenant}/${instance}/checkpinmessages`;
        new FetchProxy(apiUrl, { method: "GET", headers: { 'Content-Type': 'application/json', 'AuthToken': token } })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              throw (response.status);
            } else {
              return response.json();
            }
          }).then(response => {
            resolve(response);
          })
          .catch(error => {
            console.log(error);
            reject(null);
          });
      } else {
        reject(null);
      }
    });
  }

  public static getLinkMetadata(environment: IEnvironment, tenant: string, url: string): Promise<{ title: string, imageUrl: string, description: string }> {
    return new Promise<{ title: string, imageUrl: string, description: string }>((resolve, reject): void => {
      if (environment && tenant && url) {
        let apiUrl = `${Environment.getEnvironmentForMessagesAPI(environment)}/${tenant}/url-metadata?url=${encodeURIComponent(url)}`;
        new FetchProxy(apiUrl,
          {
            method: "GET",
            headers: {
              'Content-Type': 'application/json'
            }
          })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              throw (response.status);
            } else {
              return response.json();
            }
          }).then(response => {
            resolve(response);
          })
          .catch(error => {
            console.log(error);
            reject(null);
          });
      } else {
        reject(null);
      }
    });
  }

  public static getDigestPreview(environment: IEnvironment, tenant: string, digestData: IDigestData): Promise<{ htmlPreview: string }> {
    return new Promise<{ htmlPreview: string }>((resolve, reject): void => {
      if (environment && tenant && digestData) {
        let apiUrl = `${Environment.getEnvironmentForMessagesAPI(environment)}/${tenant}/digest/outlook/preview`;
        new FetchProxy(apiUrl,
          {
            method: "POST",
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(digestData)
          })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              throw (response.status);
            } else {
              return response.json();
            }
          }).then(response => {
            resolve(response);
          })
          .catch(error => {
            console.log(error);
            reject(null);
          });
      } else {
        reject(null);
      }
    });
  }

  public static createDigestFile(environment: IEnvironment, tenant: string, digestData: IDigestData): Promise<void> {
    return new Promise<void>((resolve, reject): void => {
      if (environment && tenant) {
        let apiUrl = `${Environment.getEnvironmentForMessagesAPI(environment)}/${tenant}/digest/outlook/generate`;
        new FetchProxy(apiUrl,
          {
            method: "POST",
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(digestData),
          })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              throw (response.status);
            } else {
              const now = new Date();
              const fileName = `digest-${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}.msg`;
              response.blob().then(blob => {
                const downloadUrl = URL.createObjectURL(blob);
                var a = document.createElement("a");
                if (typeof a.download === 'undefined') {
                  window.location.href = downloadUrl;
                } else {
                  a.href = downloadUrl;
                  a.download = fileName;
                  document.body.appendChild(a);
                  a.click();
                }
                setTimeout(() => URL.revokeObjectURL(downloadUrl), 100);
                resolve();
              });
            }
          }).then(response => {
            resolve(response);
          })
          .catch(error => {
            console.log(error);
            reject(null);
          });
      } else {
        reject(null);
      }
    });
  }
}
